'use client';
import React, { useRef } from 'react';
import { PREFERRED_SOURCE_BUTTON_HEIGHT_PX } from './constants';
import type {
  ComponentPreferredSourceButton,
  PreferredSourceButtonTheme,
} from './types';
import { usePreferredSourceButton } from './usePreferredSourceButton';

/**
 * `auto` resolves to light: dynamic-zone blocks only ever sit on the light
 * page body (CMS-2053).
 */
const DATA_THEME: Record<PreferredSourceButtonTheme, 'light' | 'dark'> = {
  auto: 'light',
  light: 'light',
  dark: 'dark',
};

export const PreferredSourceButton: React.FC<
  ComponentPreferredSourceButton
> = ({ text, theme }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const status = usePreferredSourceButton(containerRef, placeholderRef);

  // Script blocked or the button never rendered: give the space back.
  if (status === 'failed') return null;

  return (
    <div
      ref={containerRef}
      data-component="preferred-source-button"
      data-status={status}
      className="flex flex-col gap-spacing/gap/soft-12px"
    >
      {text && (
        <p className="mhg-alpha-typography-body-2 text-text/primary">{text}</p>
      )}
      <div
        style={
          status === 'rendered'
            ? undefined
            : { minHeight: PREFERRED_SOURCE_BUTTON_HEIGHT_PX }
        }
      >
        {/* No data-lang: Google renders the label in the browser language. */}
        <div
          ref={placeholderRef}
          // eslint-disable-next-line react/no-unknown-property -- publisher.js looks for this marker attribute
          google-add-preferred-source-btn=""
          data-theme={DATA_THEME[theme]}
        />
      </div>
    </div>
  );
};
